import styled from "@emotion/styled";

import { useFormatMessage, type FormatMessages } from "@skilitics/localization";

import { MediaIcon } from "../../common";
import { AiBadge } from "../../icons";
import { FONT_WEIGHT_SEMI_BOLD } from "../../utilities";
import type { MediaAssetType } from "../controls";
import { mediaIconByMediaAssetType } from "./constants";
import { MediaButtons } from "./MediaButtons";
import type { MediaButtonsProps } from "./types";

const Container = styled.div`
  user-select: none;
`;

const UnitsContainer = styled.div`
  display: flex;
  justify-content: space-between;

  padding-block-end: 16px;
`;

const Unit = styled.span`
  font-weight: ${FONT_WEIGHT_SEMI_BOLD};
`;

const DownloadButtonContainer = styled.div`
  position: absolute;
  left: 0;
  bottom: 0;

  width: 100%;

  opacity: 0;

  transition: opacity 200ms ease-in-out;
`;

const ImageContainer = styled.div`
  position: relative;
  width: 100%;
  height: auto;

  padding-block-end: 75%; // 4:3 aspect ratio
  margin-block-end: 8px;

  background-color: ${({ theme }) => theme.surface};
  background-image: ${({ theme }) => `repeating-conic-gradient(${theme.surfaceVariant} 0% 25%, transparent 0% 50%)`};
  background-size: 16px 16px;
  background-repeat: repeat;

  border: solid 1px ${({ theme }) => theme.outlineSoft};
  border-radius: ${({ theme }) => theme.borderRadius}px;

  > img {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    object-fit: contain;
    border-radius: ${({ theme }) => theme.borderRadius}px;
  }

  &:hover {
    ${DownloadButtonContainer} {
      opacity: 1;
    }
  }
`;

const MediaIconContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;

  display: flex;
  align-items: center;
  justify-content: center;

  width: 100%;
  height: 100%;
  border-radius: ${({ theme }) => theme.borderRadius}px;
`;

const StyledAiBadge = styled(AiBadge)`
  position: absolute;

  top: -2px;
  right: 5px;
`;

interface Props {
  url?: string;
  type?: MediaAssetType;
  metadata: {
    width?: number;
    height?: number;
    duration?: number;
  };
  showAiBadge?: boolean;
  onUpload?: MediaButtonsProps["onUpload"];
}

export const MediaPreview = ({ url, type, metadata: { width, height, duration }, showAiBadge, onUpload }: Props) => {
  const formatMessage = useFormatMessage(formatStrings);

  return (
    <Container>
      <ImageContainer>
        {url && (
          <>
            {url && type === "image" && <img src={url} alt="" />}
            {type && type !== "image" && (
              <MediaIconContainer>
                <MediaIcon type={mediaIconByMediaAssetType[type]} />
              </MediaIconContainer>
            )}

            <DownloadButtonContainer>
              <MediaButtons url={url} onUpload={onUpload} type={type} />
            </DownloadButtonContainer>

            {showAiBadge && <StyledAiBadge />}
          </>
        )}
      </ImageContainer>

      {width !== undefined && height !== undefined && (
        <UnitsContainer>
          <span>
            <Unit>{`${formatMessage("width")}: `}</Unit>
            <span>{`${width}px`}</span>
          </span>
          <span>
            <Unit>{`${formatMessage("height")}: `}</Unit>
            <span>{`${height}px`}</span>
          </span>
        </UnitsContainer>
      )}

      {duration !== undefined && (
        <UnitsContainer>
          <span>
            <Unit>{`${formatMessage("length")}: `}</Unit>
            <span>{`${Math.round(duration)} ${formatMessage("seconds")}`}</span>
          </span>
        </UnitsContainer>
      )}
    </Container>
  );
};

const formatStrings = {
  width: {
    en: "Width",
  },
  height: {
    en: "Height",
  },
  length: {
    en: "Length",
  },
  seconds: {
    en: "seconds", // TODO: use a lib (date-fns or something not bloated momentjs) to format something more sophisticated
  },
} satisfies FormatMessages;
